import { format } from 'date-fns';
import { Download, FileText, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { DocumentTagPicker } from './DocumentTagPicker';
import { useDocumentDownloadUrl, useUploadedDocument } from '../api/uploaded-documents-api';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface UploadedDocumentInboxSheetProps {
  documentId: string | undefined;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/**
 * Read-only-ish viewer for an Inbox uploaded document.
 *
 * The full chart-bound `UploadedDocumentSheet` requires a `patientId`
 * so it can render encounter linkage and inline metadata edits. While
 * a document still lives in the Inbox it has no patient, so we render
 * a slimmed-down view that exposes: file metadata, tag picker, and a
 * download button. After the user assigns the doc to a chart from the
 * Inbox row, the full sheet is available on the patient timeline.
 */
export function UploadedDocumentInboxSheet({ open, documentId, onOpenChange }: UploadedDocumentInboxSheetProps) {
  const { data: document } = useUploadedDocument(open ? documentId : undefined);
  const downloadUrl = useDocumentDownloadUrl(documentId);

  const handleDownload = async () => {
    const result = await downloadUrl.refetch();
    const url = result.data?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const revision = document?.current_revision;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Hide the default close — we render our own in the header.
        className="w-full sm:max-w-2xl p-6 [&>[data-slot=sheet-default-close]]:hidden">
        <SheetHeader className="p-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <SheetTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 shrink-0" />
                <span className="truncate">{document?.name ?? 'Uploaded document'}</span>
              </SheetTitle>
              <SheetDescription>
                {document ? `${document.document_type} — ${formatDocDate(document.document_date)}` : 'Loading…'}
              </SheetDescription>
            </div>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-4 pb-12">
          {revision && (
            <div className="rounded-md border bg-card p-3">
              <p className="text-sm font-medium truncate">{revision.file_name}</p>
              <p className="text-xs text-muted-foreground">
                {revision.content_type} · revision {revision.revision_number}
              </p>
              <Button
                size="sm"
                className="mt-2"
                variant="outline"
                disabled={downloadUrl.isFetching}
                onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          )}

          <Separator />

          {document && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Tags</p>
              <DocumentTagPicker kind="uploaded" documentId={document.id} />
            </div>
          )}

          {document?.notes && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Notes</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{document.notes}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatDocDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}
